import { ReactElement } from "react";
import { Site } from "@/types/sites";
import { getAllMedias, getImageUrl } from "src/utils/utils";
import { CmsMediaImageMimetype, CommonMediaAttributes } from "@/types/strapi";
import {
    ChromeTileWidths,
    AppleTouchIcons,
    Win81_Win10_IE11_Edge_TileWidths,
    FavIconPngs,
    FaviconLink,
    Win8_IE10_TileWidths,
} from "@/types/favicons";
import { getMatchingImageAttributes } from "../CmsImage/index";
import Head from "next/head";

// !! CAUTION !!
// <Head> is being deprecated, but the new replacement doesn't support custom <style> tags.
// As soon as it does, we should start to migrate to it.
// Currently using <Head> is our only option.
// https://beta.nextjs.org/docs/api-reference/file-conventions/head#supported-head-tags
// !! CAUTION !!

export const enumWidthFilter = <
    T extends
        | typeof ChromeTileWidths
        | typeof AppleTouchIcons
        | typeof Win81_Win10_IE11_Edge_TileWidths
        | typeof FavIconPngs
>(
    enumObj: T
): number[] => {
    const enumToFilter: Array<string | number> = Object.values(enumObj);
    const filteredEnum = enumToFilter.filter(
        (val: string | number): val is number => typeof val !== "string"
    );
    return filteredEnum;
};

export const getFaviconUrl = (
    favicons: CommonMediaAttributes[],
    mimeType: CmsMediaImageMimetype | CmsMediaImageMimetype[],
    pixelWidth?: number
): string => {
    const favicon = getMatchingImageAttributes(favicons, mimeType, pixelWidth);
    const faviconUrl = getImageUrl(favicon?.url);
    return faviconUrl;
};

export const getPngFaviconGroup = <
    T extends typeof AppleTouchIcons | typeof FavIconPngs
>(
    favicons: CommonMediaAttributes[],
    enumObj: T
): FaviconLink[] => {
    return enumWidthFilter<typeof enumObj>(enumObj)
        .map((pixelWidth): FaviconLink => {
            return {
                sizes: `${pixelWidth}x${pixelWidth}`,
                href: getFaviconUrl(
                    favicons,
                    CmsMediaImageMimetype.PNG,
                    pixelWidth
                ),
            };
        })
        .filter((icon: FaviconLink): boolean => icon.href !== "");
};

export const Favicons = ({ site }: { site: Site | null }): ReactElement => {
    const favicons = getAllMedias(site?.attributes?.favicon?.data ?? null);

    const appleTouchIcons = getPngFaviconGroup<typeof AppleTouchIcons>(
        favicons,
        AppleTouchIcons
    );
    const faviconPngs = getPngFaviconGroup<typeof FavIconPngs>(
        favicons,
        FavIconPngs
    );
    const faviconWindows8 = getFaviconUrl(
        favicons,
        CmsMediaImageMimetype.PNG,
        Win8_IE10_TileWidths.Medium
    );
    const faviconSvgUrl = getFaviconUrl(favicons, CmsMediaImageMimetype.SVG);
    const faviconIcoUrl = getFaviconUrl(favicons, [
        CmsMediaImageMimetype.ICO,
        CmsMediaImageMimetype.MSICO,
    ]);

    /**
     * It may be tempting to re-order these elements however I'd advise against it.
     * The order used here is the same order as specified on realfavicongenerator.net
     * I don't actually know what happens if you change the order, probably nothing, but probably best to leave it
     */
    return (
        <Head>
            {/* Default favicon
                When no favicon is set with rel=icon (or it is conditional)
                NextJS will go looking for a favicon and make a request to /favicon.ico
                These requests occasionally replace the request with the slug of the page,
                meaning we check for a campaign with a slug of "favicon.ico" instead of
                (for example) "lp293"

                To prevent this from happening, we must have a non-conditional favicon with rel=icon
            */}
            <link
                key="favicon"
                rel="icon"
                type="image/x-icon"
                href={faviconIcoUrl}
            />

            {/* iOS */}
            {appleTouchIcons.map(
                (appleTouchIcon: FaviconLink): ReactElement => {
                    return (
                        <link
                            key={`apple-touch-icon-${appleTouchIcon.sizes}`}
                            rel="apple-touch-icon"
                            sizes={appleTouchIcon.sizes}
                            href={appleTouchIcon.href}
                        />
                    );
                }
            )}

            {faviconPngs.map((favIconPng: FaviconLink): ReactElement => {
                return (
                    <link
                        key={`favicon-${favIconPng.sizes}`}
                        rel="icon"
                        type={CmsMediaImageMimetype.PNG}
                        sizes={favIconPng.sizes}
                        href={favIconPng.href}
                    />
                );
            })}

            {/* Safari */}
            {faviconSvgUrl && (
                <link
                    key="faviconSvg"
                    rel="mask-icon"
                    href={faviconSvgUrl}
                    color={site?.attributes?.primaryColour}
                />
            )}

            {faviconIcoUrl && (
                <link
                    key="favicon"
                    rel="shortcut icon"
                    type="image/x-icon"
                    href={faviconIcoUrl}
                />
            )}

            {/* Windows OS */}
            {site?.attributes?.backgroundGradientStart && (
                <meta
                    name="msapplication-TileColor"
                    content={site?.attributes?.backgroundGradientStart}
                />
            )}
            {faviconWindows8 && (
                <meta
                    name="msapplication-TileImage"
                    content={faviconWindows8}
                />
            )}
            <meta name="msapplication-config" content="/browserconfig.xml" />

            {/* Andorid OS */}
            {site?.attributes?.backgroundGradientStart && (
                <meta
                    key="favicon-theme-color"
                    name="theme-color"
                    content={site?.attributes?.backgroundGradientStart}
                />
            )}
        </Head>
    );
};
